import { supabase } from "@/integrations/supabase/client";

// ─────────────────────────────────────────────────────────────────────────────
// Account Activity log helper
//
// Activity = internal system events about the account row itself
//            (tier changes, note edits, AI verdict reviews, enrichment runs).
// Engagement = external touches (campaign sends, replies, meetings, rep notes
//              about real-world outreach) — those go to account_engagement_notes
//              or the campaign/outreach tables, NOT here.
//
// See docs/ACCOUNT_PAGE_GOLD_SHEET.md "Activity vs Engagement" section.
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical set of activity_type values we emit from the frontend. */
export type AccountActivityType =
  | "tier_changed"
  | "status_changed"
  | "reviewed"
  | "note_added"
  | "note_updated"
  | "ai_verdict_reviewed"
  | "ai_verdict_recorded" // usually emitted by DB trigger, kept here for completeness
  | "enrichment_run"
  | "contact_added";

export interface LogActivityArgs {
  accountId: string;
  activityType: AccountActivityType;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  performedBy?: { id: string | null; name: string | null };
}

/**
 * Fire-and-forget write into account_activities. Errors are logged but never
 * thrown — activity logging is a side-effect, not a critical path, and we
 * don't want a logging failure to break a save the rep just confirmed.
 */
export async function logAccountActivity({
  accountId,
  activityType,
  description,
  metadata,
  performedBy,
}: LogActivityArgs): Promise<void> {
  const { error } = await (supabase as any).from("account_activities").insert({
    account_id: accountId,
    activity_type: activityType,
    performed_by: performedBy?.name || "system",
    performed_by_id: performedBy?.id || null,
    description: description ?? null,
    metadata: metadata ?? {},
  });
  if (error) {
     
    console.warn("[useAccountActivity] log failed", activityType, error);
  }
}
